import { FIELD_NAMES as F, DEFAULT_VALUES as D } from '../constants';

export const transformMedicineData = (medicine) => {
  if (!medicine) return null;

  // Generate a unique ID if one doesn't exist
  const id = ((medicine.medicine_id || medicine.id || `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`).toString());

  return {
    id,
    name: medicine.medicine_name || medicine.name || '',
    manufacturer: medicine.manufacturer_name || medicine.manufacturer || '',
    content: medicine.content || '',
    size: medicine.size || 1,
    price: parseFloat(medicine.mrp || medicine.price || 0),
    price_to_retailer: parseFloat(medicine.price_to_retailer || 0),
    batch: medicine.batch || 'Pending',
    expiry: medicine.expiry || '',
    packing_size: medicine.packing_size || '',
    discount_tier: medicine.discount_tier || '',
    stock: medicine.stock || '-'
  };
};

export const transformDraftItem = (item) => {
  if (!item) return null;

  const medicineData = item.medicine || {
    id: (item.medicine_id || '').toString(),
    name: item.medicine_name,
    manufacturer: item.manufacturer,
    content: item.content || '',
    size: item.size || 1,
    price: item.price,
    batch: item.batch,
    expiry: item.expiry,
    packing_size: item.packing_size,
    discount_tier: item.discount_tier
  };

  return {
    medicine: transformMedicineData(medicineData),
    quantity: parseInt(item.quantity || 0),
    discount: parseFloat(item.discount || 0)
  };
};

/** Backend accepts either lat/long or pincode, never both. */
export const normalizeAddressDetailsForAPI = (addressDetails) => {
  if (!addressDetails) return { [F.ADDRESS]: D.EMPTY_STRING };

  const address = addressDetails[F.ADDRESS] || addressDetails.address || D.EMPTY_STRING;
  const latitude = addressDetails[F.LATITUDE] || addressDetails.latitude || '';
  const longitude = addressDetails[F.LONGITUDE] || addressDetails.longitude || '';
  const pincode = addressDetails[F.PINCODE] || addressDetails.pincode || '';

  if (latitude && longitude) {
    return { [F.ADDRESS]: address, [F.LATITUDE]: latitude, [F.LONGITUDE]: longitude };
  }
  if (pincode) {
    return { [F.ADDRESS]: address, [F.PINCODE]: pincode };
  }
  return { [F.ADDRESS]: address };
};

export const transformCustomerData = (customer) => {
  if (!customer) return null;

  return {
    [F.NAME]: customer[F.NAME] || D.EMPTY_STRING,
    [F.MOBILE]: customer[F.MOBILE] || D.EMPTY_STRING,
    [F.ADDRESS_DETAILS]: normalizeAddressDetailsForAPI(customer[F.ADDRESS_DETAILS]),
  };
};

export const transformDraftFromAPI = (draft) => {
  if (!draft) return null;

  return {
    _id: draft._id || draft.id,
    customer: {
      name: draft.customer?.name || '',
      mobile: draft.customer?.mobile || '',
      address_details: {
        address: draft.customer?.address_details?.address || '',
        pincode: draft.customer?.address_details?.pincode || '',
        latitude: draft.customer?.address_details?.latitude || '',
        longitude: draft.customer?.address_details?.longitude || ''
      }
    },
    pharmacy: draft.pharmacy || '',
    partner: draft.partner || '',
    customPartner: draft.custom_partner || draft.customPartner || '',
    pocName: draft.poc_name || draft.pocName || '',
    doctor: draft.doctor || '',
    items: (draft.items || []).map(transformDraftItem),
    additionalCharges: (draft.additional_charges || draft.additionalCharges || []).map(charge => ({
      type: charge.charge_type || charge.type,
      name: charge.name,
      amount: parseFloat(charge.amount || 0)
    })),
    status: draft.status || 'pending',
    createdAt: draft.created_at || draft.createdAt
  };
};

export const transformDraftForAPI = (draft) => {
  const customer = draft.customer
    ? {
        ...draft.customer,
        address_details: normalizeAddressDetailsForAPI(draft.customer.address_details),
      }
    : draft.customer;

  return {
    _id: draft._id,
    customer,
    order_id: draft.order_id,
    pharmacy: draft.pharmacy,
    partner: draft.partner || '',
    custom_partner: draft.customPartner || '',
    doctor: draft.doctor || '',
    notes: draft.notes || '',
    poc_name: draft.pocName || '',
    items: draft.items.map(item => {
      if (!item || !item.medicine) {
        console.warn('Invalid item found in draft:', item);
        return null;
      }
      
      return {
        medicine_id: item.medicine.id,
        medicine_name: item.medicine.name,
        manufacturer: item.medicine.manufacturer,
        content: item.medicine.content || '',
        size: item.medicine.size || 1,
        price: item.medicine.price,
        price_to_retailer: item.medicine.price_to_retailer || 0,
        quantity: item.quantity,
        discount: item.discount,
        batch: item.medicine.batch,
        expiry: item.medicine.expiry,
        packing_size: item.medicine.packing_size,
        discount_tier: item.medicine.discount_tier || ''
      };
    }).filter(Boolean),
    additional_charges: (draft.additionalCharges || []).map(charge => {
      if (!charge) return null;
      
      return {
        charge_type: charge.type,
        name: charge.name,
        amount: charge.amount
      };
    }).filter(Boolean),
    status: draft.status
  };
};

export const transformOrderToDraft = (orderData, parseItems, returnSplitChannel, clearOrderId = false) => {
  if (!orderData || !orderData.order) return null;
  
  const order = orderData.order;
  const cart = order.cart;
  const doctor_name = cart.billing_name?.includes("RMP Dr.") ? cart.billing_name.split("RMP Dr.")[1].trim().split(" ")[0] : '';
  
  // Extract customer details
  const customerDetails = {
    name: cart.customer_name || 'Unregistered User',
    mobile: cart.customer_phone || '',
    address_details: {
      address: cart.street_address || '',
      pincode: cart.pincode || cart.postal_code || '',
      latitude: cart.location?.latitude || '',
      longitude: cart.location?.longitude || ''
    }
  };
  
  // Transform inventory items (medicines) to draft items
  const items = cart.cart_items
    .filter(item => item.item_type === 'inventory')
    .map(item => {
      if (!item.inventory_item) return null;
      
      const medicine = item.inventory_item.medicine;
      const medicineData = {
        id: medicine.id || '',
        name: medicine.name || '',
        manufacturer: medicine.manufacturer || '',
        content: medicine.salt || '',
        size: medicine.size || 1,
        price: (item.inventory_item.mrp_pack / 100).toFixed(2),
        price_to_retailer: (item.inventory_item.ptr_pack /100).toFixed(2),
        batch: item.inventory_item.batch || 'Pending',
        expiry: item.inventory_item.expiry_date || '',
        packing_size: medicine.size_string || `1 Strip of ${medicine.size} Tablet`,
        discount_tier: medicine.discount_tier || '',
        stock: '-'
      };
      
      return {
        medicine: medicineData,
        quantity: parseInt(( item.quantity/ item.inventory_item.medicine.size )|| 0),
        discount: parseFloat((((item.discount / item.amount) * 100 ).toFixed(2)) || 0)
      };
    }).filter(Boolean);
  
  // Transform charges to additional charges
  const additionalCharges = cart.cart_items
    .filter(item => item.item_type === 'charge')
    .map(charge => {
      // Map shipping charge to delivery charge
      const type = charge.charge_type === 'SHIPPING_CHARGE' ? 'SHIPPING_CHARGE' : charge.charge_type || '';
      const name = charge.charge_type === 'SHIPPING_CHARGE' ? 'Delivery Charge' : charge.item_name || '';
      
      return {
        type: type,
        name: name,
        amount: parseFloat((charge.amount - charge.discount)/100)
      };
    });

  let partner;
  if (returnSplitChannel) {
    if (cart.address_id || cart.channel === 'APP') {
      partner = 'APP_SPLIT';
    } else if (!cart.address_id || cart.channel === 'WHATSAPP') {
      partner = 'WHATSAPP_SPLIT';
    }
  } else {
    partner = cart.channel || '';
  }
  
  return {
    customer: customerDetails,
    items: parseItems ? items : [],
    additionalCharges: parseItems ? additionalCharges : [],
    status: 'pending',
    createdAt: new Date().toISOString(),
    pharmacy: cart.serving_pharmacy_id || '',
    partner,
    customPartner: '',
    pocName: '',
    doctor: doctor_name,
    // Split drafts and explicit duplicates must not reuse the source order id
    order_id: (returnSplitChannel || clearOrderId) ? undefined : order.order_id
  };
};